import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { format, fromUnixTime } from "date-fns";
import { useSelector } from "react-redux";

import useTheme from "hooks/useTheme";
import useNotification from "hooks/useNotification";

import { findInObjectArrayByMatchingProperties } from "helpers/functions";
import TransactionInnerDetailModal from "./TransactionInnerDetailModal";

import TxDoneIcon from "theme/icons/TxDone";
import CopyIcon from "theme/icons/Copy";
import CalendarIcon from "theme/icons/Calendar";
import TAccountsIcon from "theme/icons/TAccounts";
import TransferIcon from "theme/icons/Transfer";
import FeesIcon from "theme/icons/Fees";
import CoinSentIcon from "theme/icons/CoinsSent";
import CoinReceivedIcon from "theme/icons/CoinsReceived";
import CloseIcon from "theme/icons/Close";
import AlgoExplorerIcon from "theme/icons/AlgoExplorer";

const TransactionAppCallDetailModal = ({ isOpen, setIsOpen, modalInfo }) => {
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();

	const accounts = useSelector((state) => state.Accounts);
	const accountInfo = useSelector((state) => state.AccountInfo);
	const [myAccountName, setMyAccountName] = useState(null);

	const [transactionInnerDetailModalIsOpen, setTransactionInnerDetailModalIsOpen] = useState(false);
	const [transactionInnerDetailModalInfo, setTransactionPayDetailModalInfo] = useState(false);

	useEffect(() => {
		if (modalInfo?.sender) GetMyAccountName(modalInfo);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalInfo]);

	const GetMyAccountName = () => {
		const matchedAccounts = findInObjectArrayByMatchingProperties(accounts, { "account-address": modalInfo?.sender });

		if (matchedAccounts?.length) setMyAccountName(matchedAccounts[0].name);
	};

	return (
		modalInfo?.sender && (
			<Transition appear show={isOpen} as={Fragment}>
				<Dialog as="div" className="relative z-10" onClose={() => setIsOpen(false)}>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className={`fixed inset-0 ${themeColor?.bg} bg-opacity-30`} />
					</Transition.Child>

					<div className="fixed inset-0 overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4 text-center">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								<Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
									<div className="flex flex-col w-full items-center space-y-3">
										<div className="flex w-full items-center justify-between">
											<p className={`text-md ${themeColor?.text}`}>{appLanguage?.transactionDetails}</p>
											<CloseIcon
												className="w-4 cursor-pointer"
												fill={themeColor?.hex}
												onClick={() => setIsOpen(false)}
											/>
										</div>
										<div className="flex w-full items-center justify-start space-x-5">
											<div className="flex w-full items-center space-x-3">
												<TxDoneIcon className="w-8" fill={themeColor?.hex} />
												<div className="flex flex-col">
													<p className={`text-sm ${themeColor?.text}`}>Tx #</p>
													<p className={`text-xs ${themeColor?.text}`}>
														{modalInfo?.id.slice(0, 5) + "..." + modalInfo?.id.slice(-5)}
													</p>
												</div>
											</div>
											<div className="flex items-center justify-end space-x-3">
												<CopyIcon
													className="w-4 cursor-pointer"
													fill={themeColor?.hex}
													onClick={() => {
														navigator.clipboard.writeText(modalInfo?.id);
														notify("success", "Copied to clipboard");
													}}
												/>
												<AlgoExplorerIcon
													className={`w-4 cursor-pointer`}
													fill={themeColor?.hex}
													onClick={() =>
														window.open(`https://algoexplorer.io/tx/${modalInfo?.id}`, "_blank")
													}
												/>
											</div>
										</div>

										<div
											className={`flex flex-col w-full items-center justify-center space-y-3 rounded-lg p-3 border ${themeColor?.border}`}
										>
											<div className="flex w-full items-center justify-between">
												<div className="flex w-fit space-x-2">
													<CalendarIcon className={`w-4`} fill={themeColor?.hex} />
													<p className={`text-md ${themeColor?.text}`}>{appLanguage?.date}</p>
												</div>
												<p className={`text-sm ${themeColor?.text}`}>
													{modalInfo["round-time"] &&
														format(fromUnixTime(modalInfo["round-time"]), "MMM dd, yyyy kk:mm:ss")}
												</p>
											</div>
											<div className="flex w-full items-center justify-between">
												<div className="flex w-fit space-x-2">
													<TAccountsIcon className={`w-4`} fill={themeColor?.hex} />
													<p className={`text-md ${themeColor?.text}`}>{appLanguage?.myAccount}</p>
												</div>
												<p className={`text-sm ${themeColor?.text}`}>{myAccountName}</p>
											</div>
											<div className="flex w-full items-center justify-between">
												<div className="flex w-fit space-x-2">
													<TransferIcon className={`w-4`} fill={themeColor?.hex} />
													<p className={`text-md ${themeColor?.text}`}>{appLanguage?.type}</p>
												</div>
												<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.appCall}</p>
											</div>
											<div className="flex w-full items-center justify-between">
												<div className="flex w-fit space-x-2">
													<FeesIcon className={`w-4`} fill={themeColor?.hex} />
													<p className={`text-md ${themeColor?.text}`}>{appLanguage?.fees}</p>
												</div>
												<p className={`text-sm ${themeColor?.text}`}>{modalInfo?.fee / 1000000}</p>
											</div>
											<div className="flex w-full items-center justify-between">
												<p className={`text-md ${themeColor?.text}`}>Inner Txns</p>
												<p
													className={`text-sm cursor-pointer`}
													onClick={() => {
														setTransactionPayDetailModalInfo(modalInfo);
														setTransactionInnerDetailModalIsOpen(true);
													}}
												>
													{`Show ${modalInfo?.["inner-txns"]?.length} inner txns`}
												</p>
											</div>
											<div className="flex w-full items-center justify-between">
												<p className={`text-md ${themeColor?.text}`}>Application ID</p>
												<p className={`text-sm ${themeColor?.text}`}>
													{modalInfo["application-transaction"]["application-id"]}
												</p>
											</div>
											<div className="flex w-full items-center justify-between">
												<p className={`text-md ${themeColor?.text}`}>OnCompletion</p>
												<p className={`text-sm ${themeColor?.text}`}>
													{modalInfo["application-transaction"]["on-completion"]}
												</p>
											</div>
											<div className="flex flex-col w-full items-start justify-between">
												<p className={`text-md ${themeColor?.text}`}>Assets</p>
												<div className="flex flex-col w-full">
													{modalInfo?.["inner-txns"]?.length &&
														modalInfo["inner-txns"].map((innerTxn, index) =>
															// if transaction type is TxType
															innerTxn["tx-type"] === "axfer" &&
															innerTxn["asset-transfer-transaction"]["amount"] > 0 ? (
																innerTxn["asset-transfer-transaction"]["receiver"] ===
																	innerTxn.sender || innerTxn.sender !== accountInfo.address ? (
																	<div
																		className="flex w-full items-center justify-between"
																		key={index}
																	>
																		<div className="flex items-center space-x-2">
																			<CoinReceivedIcon
																				className={`w-5`}
																				fill={themeColor?.hex}
																			/>
																			<p
																				className={`text-sm font-bold ${themeColor?.text}`}
																			>
																				{appLanguage?.received}
																			</p>
																		</div>
																		<div className="flex items-center space-x-2">
																			<p
																				className={`text-sm ${themeColor?.text}`}
																				key={index}
																			>
																				{innerTxn["asset-transfer-transaction"][
																					"amount"
																				] /
																					Math.pow(
																						10,
																						innerTxn["asset-transfer-transaction"][
																							"asset-decimals"
																						]
																					)}{" "}
																				{
																					innerTxn["asset-transfer-transaction"][
																						"asset-unit-name"
																					]
																				}
																			</p>
																			<img
																				className={`w-4 h-4 ${themeColor?.text}`}
																				alt="asset"
																				loading="lazy"
																				src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${innerTxn["asset-transfer-transaction"]["asset-id"]}&width=20&`}
																			/>
																		</div>
																	</div>
																) : (
																	<div className="flex items-center space-x-2" key={index}>
																		<div className="flex items-center space-x-2">
																			<CoinSentIcon className={`w-5`} fill="#FC4242" />
																			<p className={`text-sm font-bold text-candy-red`}>
																				Sent
																			</p>
																		</div>

																		<div className="flex items-center space-x-2">
																			<p className={`text-sm text-candy-red`} key={index}>
																				-
																				{innerTxn["asset-transfer-transaction"][
																					"amount"
																				] /
																					Math.pow(
																						10,
																						innerTxn["asset-transfer-transaction"][
																							"asset-decimals"
																						]
																					)}
																				}{" "}
																				{
																					innerTxn["asset-transfer-transaction"][
																						"asset-unit-name"
																					]
																				}
																			</p>
																			<img
																				className={`w-4 h-4 ${themeColor?.text}`}
																				alt="asset"
																				loading="lazy"
																				src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${innerTxn["asset-transfer-transaction"]["asset-id"]}&width=20`}
																			/>
																		</div>
																	</div>
																)
															) : // if transaction type is pay
															innerTxn["tx-type"] === "pay" &&
															  innerTxn["payment-transaction"]["amount"] > 0 ? (
																innerTxn["payment-transaction"]["receiver"] === innerTxn.sender ||
																innerTxn.sender !== accountInfo.address ? (
																	<div
																		className="flex w-full items-center justify-between"
																		key={index}
																	>
																		<div className="flex items-center space-x-2">
																			<CoinReceivedIcon
																				className={`w-5`}
																				fill={themeColor?.hex}
																			/>
																			<p
																				className={`text-sm font-bold ${themeColor?.text}`}
																			>
																				Received
																			</p>
																		</div>
																		<div className="flex items-center space-x-2">
																			<p
																				className={`text-sm ${themeColor?.text}`}
																				key={index}
																			>
																				{innerTxn["payment-transaction"]["amount"] /
																					1000000}{" "}
																				Algo
																			</p>
																			<img
																				className={`w-4 h-4 ${themeColor?.text}`}
																				alt="asset"
																				loading="lazy"
																				src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=-1&width=20`}
																			/>
																		</div>
																	</div>
																) : (
																	<div className="flex items-center space-x-2" key={index}>
																		<div className="flex items-center space-x-2">
																			<CoinSentIcon className={`w-5`} fill="#FC4242" />
																			<p className={`text-sm font-bold text-candy-red`}>
																				Sent
																			</p>
																		</div>

																		<div className="flex items-center space-x-2">
																			<p className={`text-sm text-candy-red`} key={index}>
																				-
																				{innerTxn["payment-transaction"]["amount"] /
																					1000000}{" "}
																				Algo
																			</p>
																			<img
																				className={`w-4 h-4 ${themeColor?.text}`}
																				alt="asset"
																				loading="lazy"
																				src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=-1&width=20`}
																			/>
																		</div>
																	</div>
																)
															) : (
																<></>
															)
														)}
												</div>
											</div>
											<div className="flex flex-col w-full items-start justify-start">
												<div className="flex w-fit space-x-2">
													{/* <UserIconRoundIcon className={`w-4`} fill={themeColor?.hex} /> */}
													<p className={`text-md ${themeColor?.text}`}>{appLanguage?.notes}</p>
												</div>
												<p className={`text-xs break-all`}>{modalInfo?.note && atob(modalInfo.note)}</p>
											</div>
										</div>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>

					<TransactionInnerDetailModal
						isOpen={transactionInnerDetailModalIsOpen}
						setIsOpen={setTransactionInnerDetailModalIsOpen}
						modalInfo={transactionInnerDetailModalInfo}
					/>
				</Dialog>
			</Transition>
		)
	);
};

export default TransactionAppCallDetailModal;
